import type { TableViewSelectionWithType } from './selection';
import { TableViewRowSelection } from './selection';

export interface TableCell {
  rowId: string;
}

export type ColumnAccessor<T extends TableCell> = (cell: T) =>
  | {
      valueSetFromString(rowId: string, value: string): void;
      /** Column type string (e.g. 'select', 'multi-select', 'text') */
      type?: string;
      /**
       * Seed for the select/multi-select popover's search input when
       * editing is triggered by a single printable keypress. The
       * cell renderer consumes this once in `afterEnterEditingMode`.
       * Without seeding, the triggering keystroke is dropped — the
       * popover's input only focuses on the next animation frame,
       * by which time the original event has already bubbled away.
       */
      setInitialEditChar?(char: string): void;
    }
  | undefined;

export interface StartEditOptions<T extends TableCell> {
  event: KeyboardEvent;
  selection: TableViewSelectionWithType | undefined;
  getCellContainer: (
    groupKey: string | undefined,
    rowIndex: number,
    columnIndex: number
  ) => T | undefined;
  updateSelection: (sel: TableViewSelectionWithType) => void;
  getColumn: ColumnAccessor<T>;
}

export function handleCharStartEdit<T extends TableCell>(
  options: StartEditOptions<T>
): boolean {
  const { event, selection, getCellContainer, updateSelection, getColumn } =
    options;

  const target = event.target as HTMLElement | null;
  if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
    return false;
  }

  if (
    selection &&
    !TableViewRowSelection.is(selection) &&
    !selection.isEditing &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.altKey &&
    event.key.length === 1
  ) {
    const cell = getCellContainer(
      selection.groupKey,
      selection.focus.rowIndex,
      selection.focus.columnIndex
    );
    if (cell) {
      const column = getColumn(cell);
      if (column) {
        // Select/multi-select: don't call valueSetFromString — that
        // would persist the keystroke as a new tag. Instead seed the
        // popover's search input with the character so the keystroke
        // surfaces as text in the filter field when the popover opens.
        const colType = column.type;
        if (colType === 'select' || colType === 'multi-select') {
          column.setInitialEditChar?.(event.key);
        } else {
          column.valueSetFromString(cell.rowId, event.key);
        }
      }
      updateSelection({ ...selection, isEditing: true });
      event.preventDefault();
      return true;
    }
  }
  return false;
}
